import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Divider,
  Link,
  Fade,
  Slide,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon,
  AutoAwesome as AIIcon,
  Assessment as ReportIcon,
  Devices as DeviceIcon,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';
import LoadingOverlay from '../components/LoadingOverlay';
import { useAuth } from '../contexts/AuthContext';

const HomePage: React.FC = () => {
  const { currentUser, isAuthenticating, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Show auth modal on first visit if user is not authenticated
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisited');
    if (!hasVisited && !currentUser) {
      setAuthModalOpen(true);
      localStorage.setItem('hasVisited', 'true');
    }
  }, [currentUser]);

  // Handle navigation to dashboard with smooth transition
  useEffect(() => {
    if (currentUser && !isAuthenticating) {
      // Start fade out
      setShowContent(false);
      // Navigate after fade completes
      setTimeout(() => {
        navigate('/dashboard');
      }, 300);
    }
  }, [currentUser, navigate, isAuthenticating]);

  const handleAuthSuccess = () => {
    console.log('Auth successful!');
  };

  const handleGetStarted = () => {
    if (!currentUser) {
      setAuthModalOpen(true);
    } else {
      console.log('User is authenticated, proceeding to analysis...');
    }
  };

  const handleScrollTo = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Get loading message based on auth state
  const getLoadingMessage = () => {
    if (isAuthenticating) {
      return {
        message: "Signing you in...",
        subMessage: "Just a moment while we authenticate your account"
      };
    }
    if (isLoggingOut) {
      return {
        message: "Signing you out...",
        subMessage: "Thank you for using Field Sense AI"
      };
    }
    return { message: "Loading..." };
  };

  const loadingInfo = getLoadingMessage();

  const services = [
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Wildlife Data Analysis",
      description: "Advanced AI analysis of audio recordings, images, and other ecological data using cutting-edge machine learning models.",
      features: ["Audio species identification", "Image recognition", "Data pattern analysis", "Custom model training"]
    },
    {
      icon: <ReportIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Automated Reporting",
      description: "Generate comprehensive reports automatically from your ecological data with customizable templates and insights.",
      features: ["PDF report generation", "Custom templates", "Data visualization", "Statistical analysis"]
    },
    {
      icon: <DeviceIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Device Integration",
      description: "Seamless integration with AudioMoth and other ecological monitoring devices for streamlined data collection.",
      features: ["AudioMoth compatibility", "Bulk file processing", "Cloud storage", "Real-time sync"]
    },
    {
      icon: <AIIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Multiple AI Models",
      description: "Access to various specialized AI models including BirdNET Global, regional variants, and custom trained models.",
      features: ["BirdNET Global v2.4", "Regional species models", "Custom model deployment", "Model comparison tools"]
    }
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: "0",
      description: "Perfect for researchers getting started",
      features: [
        "Basic BirdNET analysis",
        "Up to 10 files per month",
        "Standard reporting",
        "Community support",
        "Basic export options"
      ],
      buttonText: "Get Started Free",
      popular: false
    },
    {
      name: "Pro",
      price: "29",
      description: "Advanced features for professionals",
      features: [
        "All AI models included",
        "Unlimited file processing",
        "Advanced reporting",
        "Priority support",
        "Custom templates",
        "API access",
        "Team collaboration"
      ],
      buttonText: "Start Pro Trial",
      popular: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      description: "Tailored solutions for organizations",
      features: [
        "Custom model training",
        "Dedicated support",
        "On-premise deployment",
        "Custom integrations",
        "Advanced analytics",
        "SLA guarantees",
        "Training & onboarding"
      ],
      buttonText: "Contact Sales",
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What file formats does Field Sense AI support?",
      answer: "We support all common audio formats including WAV, MP3, FLAC, and M4A. For images, we support JPEG, PNG, and TIFF formats. Our platform is optimized for AudioMoth recordings but works with any ecological data files."
    },
    {
      question: "How accurate are the AI models?",
      answer: "Our AI models achieve industry-leading accuracy rates. BirdNET Global v2.4 has over 95% accuracy for common species, while our regional models can achieve even higher accuracy for local species identification."
    },
    {
      question: "Can I use Field Sense AI for commercial projects?",
      answer: "Yes! Our Pro and Enterprise plans are designed for commercial use. We offer flexible licensing options and can work with you to create custom solutions for your specific needs."
    },
    {
      question: "Is my data secure and private?",
      answer: "Absolutely. We use enterprise-grade security measures including encrypted storage, secure data transmission, and compliance with major data protection regulations. Your data remains private and is never shared without your explicit consent."
    },
    {
      question: "Do you offer device rental programs?",
      answer: "Yes, we partner with leading ecological monitoring device manufacturers to offer rental programs for AudioMoth and other field recording equipment. Contact us for availability and pricing."
    }
  ];

  return (
    <Box sx={{ bgcolor: '#1a1a1a', minHeight: '100vh', color: 'white' }}>
      <Navbar onLoginClick={() => setAuthModalOpen(true)} />
      
      {/* Loading Overlay */}
      <LoadingOverlay
        open={isAuthenticating || isLoggingOut}
        message={loadingInfo.message}
        subMessage={loadingInfo.subMessage}
      />
      
      {/* Hero Section */}
      <Fade in={showContent} timeout={300}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
            pt: { xs: 12, md: 16 },
            pb: { xs: 8, md: 12 },
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Background Pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'radial-gradient(circle at 25% 25%, #00C85330 0%, transparent 50%)',
              pointerEvents: 'none'
            }}
          />
          
          <Container maxWidth="lg">
            <Slide in={showContent} direction="up" timeout={500}>
              <Box textAlign="center" sx={{ position: 'relative', zIndex: 1 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '4rem', lg: '5rem' },
                    fontWeight: 800,
                    mb: 2,
                    background: 'linear-gradient(45deg, #ffffff, #00C853)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.1
                  }}
                >
                  Field Sense AI
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: '1.2rem', md: '1.8rem', lg: '2.2rem' },
                    fontWeight: 300,
                    mb: 4,
                    color: 'rgba(255,255,255,0.9)',
                    maxWidth: '800px',
                    mx: 'auto'
                  }}
                >
                  AI-Powered Ecological Analysis
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1rem', md: '1.3rem' },
                    fontWeight: 400,
                    mb: 6,
                    color: 'rgba(255,255,255,0.7)',
                    maxWidth: '600px',
                    mx: 'auto',
                    lineHeight: 1.6
                  }}
                >
                  Analyze your ecological data with the latest AI models, generate automated reports, and gain insights from your wildlife recordings?all in one powerful platform.
                </Typography>
                
                <Box display="flex" gap={3} justifyContent="center" flexWrap="wrap">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGetStarted}
                    disabled={isAuthenticating || isLoggingOut}
                    sx={{
                      py: 2,
                      px: 5,
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      textTransform: 'none',
                      boxShadow: '0 8px 32px rgba(0, 200, 83, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 12px 40px rgba(0, 200, 83, 0.4)',
                        bgcolor: '#00A847'
                      }
                    }}
                  >
                    Get Started Free
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleScrollTo('how-it-works')}
                    sx={{
                      py: 2,
                      px: 5,
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'white',
                      textTransform: 'none',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: 'rgba(0, 200, 83, 0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>
            </Slide>
          </Container>
        </Box>
      </Fade>

      {/* Services Section */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#222222' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 6,
              color: 'white'
            }}
          >
            How It Works
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: '#2d2d2d',
                    border: '1px solid rgba(255,255,255,0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(0, 200, 83, 0.2)',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box mb={3}>{service.icon}</Box>
                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'white' }}>
                      {service.title}
                    </Typography>
                    <Typography variant="body1" paragraph sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                      {service.description}
                    </Typography>
                    <Box>
                      {service.features.map((feature, idx) => (
                        <Box key={idx} display="flex" alignItems="center" mb={1}>
                          <CheckIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mr: 1 }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#1a1a1a' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 6,
              color: 'white'
            }}
          >
            Choose Your Plan
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {pricingPlans.map((plan, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    bgcolor: plan.popular ? '#2d2d2d' : '#252525',
                    border: plan.popular ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(255,255,255,0.1)',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: plan.popular ? '0 12px 40px rgba(0, 200, 83, 0.3)' : '0 12px 40px rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        fontWeight: 600
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'white' }}>
                      {plan.name}
                    </Typography>
                    <Box mb={2}>
                      <Typography
                        variant="h3"
                        component="span"
                        sx={{ fontWeight: 800, color: theme.palette.primary.main }}
                      >
                        {plan.price === 'Custom' ? plan.price : `$${plan.price}`}
                      </Typography>
                      {plan.price !== 'Custom' && (
                        <Typography variant="body1" component="span" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                          /month
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body1" paragraph sx={{ color: 'rgba(255,255,255,0.8)', mb: 4 }}>
                      {plan.description}
                    </Typography>
                    <Button
                      variant={plan.popular ? "contained" : "outlined"}
                      fullWidth
                      size="large"
                      onClick={handleGetStarted}
                      sx={{
                        py: 1.5,
                        mb: 4,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        ...(plan.popular ? {
                          bgcolor: theme.palette.primary.main,
                          '&:hover': { bgcolor: '#00A847' }
                        } : {
                          borderColor: 'rgba(255,255,255,0.3)',
                          color: 'white',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: 'rgba(0, 200, 83, 0.1)'
                          }
                        })
                      }}
                    >
                      {plan.buttonText}
                    </Button>
                    <Box textAlign="left">
                      {plan.features.map((feature, idx) => (
                        <Box key={idx} display="flex" alignItems="center" mb={1.5}>
                          <CheckIcon sx={{ fontSize: 18, color: theme.palette.primary.main, mr: 2 }} />
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* About Section */}
      <Box id="about" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#222222' }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                gutterBottom
                sx={{
                  fontSize: { xs: '2rem', md: '3rem' },
                  fontWeight: 700,
                  color: 'white',
                  mb: 4
                }}
              >
                About Field Sense AI
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', lineHeight: 1.7 }}>
                Field Sense AI was born from the need to democratize ecological research and make advanced AI tools accessible to researchers, conservationists, and organizations worldwide.
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', lineHeight: 1.7 }}>
                Our platform combines cutting-edge machine learning models with intuitive interfaces, allowing users to analyze complex ecological data without requiring deep technical expertise.
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '1.1rem', lineHeight: 1.7 }}>
                We believe that better data leads to better conservation decisions, and our mission is to provide the tools that make this possible.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: '#2d2d2d',
                  p: 4,
                  borderRadius: 3,
                  border: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                        10K+
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Files Analyzed
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                        95%
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Accuracy Rate
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                        50+
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Species Models
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box textAlign="center">
                      <Typography variant="h3" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
                        24/7
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        Support
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#1a1a1a' }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 6,
              color: 'white'
            }}
          >
            Frequently Asked Questions
          </Typography>
          {faqs.map((faq, index) => (
            <Accordion
              key={index}
              sx={{
                bgcolor: '#2d2d2d',
                color: 'white',
                mb: 2,
                border: '1px solid rgba(255,255,255,0.1)',
                '&:before': { display: 'none' },
                borderRadius: 2,
                '&.Mui-expanded': {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                sx={{ py: 2 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.7 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact" sx={{ py: { xs: 8, md: 12 }, bgcolor: '#222222' }}>
        <Container maxWidth="md">
          <Typography
            variant="h2"
            align="center"
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 6,
              color: 'white'
            }}
          >
            Get In Touch
          </Typography>
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#2d2d2d',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 3
            }}
          >
            <EmailIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 3 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'white' }}>
              Ready to Get Started?
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: 'rgba(255,255,255,0.8)', mb: 4 }}>
              Have questions about our platform or need help getting started? We're here to help.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component="a"
              href="mailto:support@fieldsenseai.com"
              sx={{
                py: 2,
                px: 4,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: theme.palette.primary.main,
                '&:hover': { bgcolor: '#00A847' }
              }}
            >
              Contact Support
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, bgcolor: '#1a1a1a', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Copyright 2025 Field Sense AI. All rights reserved.
          </Typography>
        </Container>
      </Box>

      {/* Auth Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </Box>
  );
};

export default HomePage;

